// monaco/hovers.js
import { ruleLanguageSpec } from "../spec/languageSpec.js";

/**
 * Operator documentation
 */
const OP_DOCS = new Map([
    ["==", "**Equality**: compares same-typed values (d/s/b)."],
    ["!=", "**Inequality**: compares same-typed values (d/s/b)."],
    [">", "**Greater than**: numeric `%d`."],
    [">=", "**Greater or equal**: numeric `%d`."],
    ["<", "**Less than**: numeric `%d`."],
    ["<=", "**Less or equal**: numeric `%d`."],
    ["<@", "**Membership**: left `%s` ∈ right `%s[` (string list)."],
    ["!<@", "**Non-membership**: left `%s` ∉ right `%s[`."],
    ["&", "**AND**: logical conjunction."],
    ["|", "**OR**: logical disjunction."],
]);
// eslint-disable-next-line valid-jsdoc
/**
 * Helper functions for line and position handling
 */
function lineInfo(model, position) {
    const lineNumber = position.lineNumber;
    const lineText = model.getLineContent(lineNumber);
    return { lineNumber, lineText };
}

// Clamp range [start, end) safely within line length
function clampRange(start, end, max) {
    const s = Math.max(0, Math.min(start, max));
    const e = Math.max(s, Math.min(end, max));
    return [s, e];
}

// Convert zero-based indices into Monaco Range
function toRange(monaco, lineNumber, startCol0, endCol0) {
    // Monaco uses 1-based columns
    return {
        range: new monaco.Range(lineNumber, startCol0 + 1, lineNumber, endCol0 + 1),
        startColumn: startCol0 + 1,
        endColumn: endCol0 + 1,
    };
}

// eslint-disable-next-line valid-jsdoc
/**
 * Detect an operator from the spec near the cursor.
 * Returns { lexeme, range } if found, otherwise null.
 */
function detectOperatorAt(monaco, model, position) {
    const { lineNumber, lineText } = lineInfo(model, position);
    const col0 = position.column - 1; // 0-based
    if (!lineText) return null;

    // Sort operators by length (longest first)
    const ops = [...ruleLanguageSpec.operators]
        .map((o) => o.lexeme)
        .sort((a, b) => b.length - a.length);

    // Search window around cursor: +/- 3 chars is enough
    const windowRadius = 3;
    const [winStart, winEnd] = clampRange(
        col0 - windowRadius,
        col0 + windowRadius + 1,
        lineText.length
    );
    const windowText = lineText.slice(winStart, winEnd);

    for (const lex of ops) {
        const idx = windowText.indexOf(lex);
        if (idx === -1) continue;
        const absStart = winStart + idx;
        const absEnd = absStart + lex.length;

        // Cursor must be inside or right next to the operator
        if (col0 >= absStart && col0 <= absEnd) {
            const { range } = toRange(monaco, lineNumber, absStart, absEnd);
            return { lexeme: lex, range };
        }
    }
    return null;
}
// eslint-disable-next-line valid-jsdoc
/**
 * Detect type %d/%s/%b under the cursor.
 * Returns { typeLex, range } or null.
 */
function detectTypeAt(monaco, model, position) {
    const { lineNumber, lineText } = lineInfo(model, position);
    const col0 = position.column - 1; // 0-based
    if (!lineText) return null;

    // Look at 2 chars around the cursor
    const start = Math.max(0, col0 - 1);
    const end = Math.min(lineText.length, col0 + 1);
    const seg = lineText.slice(start, end);

    const match =
        /%([dsb])/.exec(seg) ||
        /%([dsb])/.exec(lineText.slice(Math.max(0, col0 - 2), Math.min(lineText.length, col0 + 2)));

    if (!match) return null;

    const mIndex = lineText.indexOf(`%${match[1]}`, Math.max(0, col0 - 2));
    if (mIndex === -1) return null;

    const { range } = toRange(monaco, lineNumber, mIndex, mIndex + 2);
    return { typeLex: `%${match[1]}`, range };
}
// eslint-disable-next-line valid-jsdoc
/**
 * Detect variable under the cursor.
 * Supports:
 *   $name
 *   $name%t
 *   $`name with spaces`%t
 *   identifiers with braces: approve_rate_{gatewayname}
 * Returns { name, typeChar|null, range } or null.
 */
function detectVariableAt(monaco, model, position) {
    const { lineNumber, lineText } = lineInfo(model, position);
    const col0 = position.column - 1; // 0-based
    if (!lineText) return null;

    let l = col0;
    let r = col0;

    // Allowed chars in variable names (excluding $ and %)
    const varBodyChar = /[A-Za-z0-9_{}]/;

    // Expand left
    while (l > 0) {
        const ch = lineText[l - 1];
        if (ch === '$' || ch === '`' || varBodyChar.test(ch)) {
            l--;
            continue;
        }
        if (ch === '%') {
            l--;
            continue;
        }
        break;
    }

    // Expand right
    while (r < lineText.length) {
        const ch = lineText[r];
        if (ch === '`' || varBodyChar.test(ch)) {
            r++;
            continue;
        }
        if (ch === '%' || ch === '[') {
            r++;
            continue;
        }
        if (ch === 'd' || ch === 's' || ch === 'b') {
            r++;
            continue;
        }
        break;
    }

    if (l >= r) return null;

    const segment = lineText.slice(l, r);

    // Match forms:
    // 1) $name, $name%t, $name%t[
    // 2) $`any text`, $`any text`%t, $`any text`%t[
    const m =
        segment.match(/\$(?:`([^`]+)`|([A-Za-z_][A-Za-z0-9_{}]*))(?:%([dsb])(\[)?|)/);

    if (!m) return null;

    const name = (m[1] ?? m[2] ?? "").trim();
    const typeChar = m[3] ?? null;

    const segStartInLine = lineText.indexOf(segment, l);
    const varStartInSeg = segment.indexOf(m[0]);
    const absStart = segStartInLine + varStartInSeg;
    const absEnd = absStart + m[0].length;

    const { range } = toRange(monaco, lineNumber, absStart, absEnd);
    return { name, typeChar, range };
}
// eslint-disable-next-line valid-jsdoc
/**
 * Public API
 *
 * registerHovers(langId, { varDocs, varTypes })
 *  - varDocs: Map(name -> markdown string)
 *  - varTypes: Map(name -> 'd'|'s'|'b')
 */
export function registerHovers(monaco, langId, opts = {}) {
    const { varDocs = new Map(), varTypes = new Map() } = opts;

    return monaco.languages.registerHoverProvider(langId, {
        provideHover(model, position) {
            // 1) Operators
            const op = detectOperatorAt(monaco, model, position);
            if (op) {
                const doc = OP_DOCS.get(op.lexeme) || `Operator: \`${op.lexeme}\``;
                return {
                    contents: [{ value: `### \`${op.lexeme}\`` }, { value: doc }],
                    range: op.range,
                };
            }

            // 2) Types %d/%s/%b
            const t = detectTypeAt(monaco, model, position);
            if (t) {
                let tip = "";
                if (t.typeLex === "%d") tip = "**%d** — number (int/decimal), negatives allowed.";
                else if (t.typeLex === "%s") tip = "**%s** — string. Lists use **%s[** .";
                else if (t.typeLex === "%b") tip = "**%b** — boolean. Examples: `true%b`, `yes%b`, `1%b`.";
                if (tip) {
                    return { contents: [{ value: tip }], range: t.range };
                }
            }

            // 3) Variables
            const v = detectVariableAt(monaco, model, position);
            if (v && v.name) {
                const declaredT = v.typeChar || varTypes.get(v.name) || "?";
                const head = `### \`$${v.name}%${declaredT}\``;

                const doc = varDocs.get(v.name);
                if (doc) {
                    return {
                        contents: [{ value: head }, { value: doc }],
                        range: v.range,
                    };
                }

                return {
                    contents: [{ value: head }, { value: "Add type suffix if missing: `%d` | `%s` | `%b`." }],
                    range: v.range,
                };
            }

            return null;
        },
    });
}
